import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ElementRef,
} from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import * as Location from 'expo-location';
import { MapPin, Pencil } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { reverseGeocode } from '../../utils/geocode';
import { getAccuratePosition, isCoarseFix, metersToFeetLabel } from '../../utils/location';
import { LocationPickerBody } from './LocationPickerBody';
import { usePanelStack } from '../layout/PanelStack';
import type { Structure } from '../../types';

export interface PostLocation {
  latitude: number;
  longitude: number;
  address?: string;
  // Set only on a GPS fix, so a coarse pin stays identifiable after the fact.
  // A manual pick through LocationPickerBody leaves it undefined.
  accuracyM?: number | null;
}

interface Props {
  linkedStructure: Structure | null;
  // True when a structureId came in via route param — disables GPS fetch so
  // a slow-loading store structure doesn't get overwritten.
  hasStructureParam: boolean;
  userLocation: PostLocation | null;
  onChangeLocation: (loc: PostLocation) => void;
  // False on post edit — the mount effect would otherwise clobber the
  // post's stored location with a fresh GPS fix. Defaults true (create flow).
  autoLocate?: boolean;
  // React 19 takes `ref` as a plain prop — no forwardRef wrapper needed.
  ref?: React.Ref<PostLocationSectionHandle>;
}

export interface PostLocationSectionHandle {
  /** Open the map picker from outside — the composer's "Set location" path. */
  openPicker: () => void;
}

/**
 * Location row + the effect that picks an initial location from (in
 * priority order): a linked structure's centroid, or the device's GPS
 * reverse-geocoded to a street address. A manual pick always wins over GPS
 * for the rest of the screen's life.
 *
 * The picker is a PanelStack push (panel-system.md Phase 3, "the checklist
 * and before-post pickers become drill-ins on every platform") — this
 * section must therefore only ever render inside PostComposer's PanelStack.
 */
export function PostLocationSection({
  linkedStructure,
  hasStructureParam,
  userLocation,
  onChangeLocation,
  autoLocate = true,
  ref,
}: Props) {
  const { colors } = useTheme();
  const { push, pop } = usePanelStack();
  const rowRef = useRef<ElementRef<typeof TouchableOpacity>>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [gpsAccuracyM, setGpsAccuracyM] = useState<number | null>(null);
  // Set when the GPS fetch rejects (e.g. no accurate-enough fix within the
  // timeout) — distinguishes "never tried" from "tried and failed" so the
  // row can prompt the user to place the pin manually.
  const [gpsFailed, setGpsFailed] = useState(false);
  const manualLocationRef = useRef(false);

  const openPicker = useCallback(() => {
    push({
      key: 'location',
      title: 'Set observation location',
      triggerRef: rowRef,
      render: () => (
        <LocationPickerBody
          initial={userLocation}
          onConfirm={(loc) => {
            manualLocationRef.current = true;
            setGpsAccuracyM(null);
            setGpsFailed(false);
            onChangeLocation(loc);
            pop();
          }}
        />
      ),
    });
  }, [push, pop, userLocation, onChangeLocation]);

  useImperativeHandle(ref, () => ({ openPicker }), [openPicker]);

  useEffect(() => {
    let stale = false;

    if (linkedStructure) {
      // Linking a structure always wins over a manual location pick.
      manualLocationRef.current = false;
      setGpsAccuracyM(null);
      setGpsFailed(false);
      onChangeLocation({
        latitude: linkedStructure.centroid.latitude,
        longitude: linkedStructure.centroid.longitude,
        address: linkedStructure.address || undefined,
      });
    } else if (autoLocate && !hasStructureParam && !manualLocationRef.current) {
      // Only fetch GPS if there's no structure param at all — avoids the race
      // where storeStructure hasn't loaded yet but paramStructureId is set.
      (async () => {
        setIsLocating(true);
        setGpsFailed(false);
        try {
          const { status } = await Location.requestForegroundPermissionsAsync();
          if (status !== 'granted' || stale || manualLocationRef.current) return;

          // acceptCoarse: a scout out of cell range gets no A-GPS, so accuracy
          // converges slowly and the strict 25m gate was throwing away fixes
          // that would have placed the pin on the right street. An approximate
          // pin the hint labels and the user can nudge beats no location at
          // all — measured at 12% of a day's posts (2026-07-30).
          const fix = await getAccuratePosition({ acceptCoarse: true });
          // A manual pick made mid-acquisition (the GPS fetch can take up to
          // the 60s timeout) must not get silently overwritten once it lands.
          if (stale || manualLocationRef.current) return;
          const address = await reverseGeocode(fix.latitude, fix.longitude);
          if (stale || manualLocationRef.current) return;

          setGpsAccuracyM(fix.accuracyM);
          onChangeLocation({
            latitude: fix.latitude,
            longitude: fix.longitude,
            address: address || undefined,
            accuracyM: fix.accuracyM,
          });
        } catch {
          // No accurate-enough fix (or permission/geolocation error) — leave
          // location null and let the row prompt for a manual pin drop.
          if (!stale && !manualLocationRef.current) setGpsFailed(true);
        } finally {
          if (!stale) setIsLocating(false);
        }
      })();
    }

    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedStructure, hasStructureParam, autoLocate]);

  return (
    // The wrapper owns the gap below this section — the hint under the row is
    // conditional (absent while locating / after a clean manual pick), and
    // hanging the margin off it let the next panel sit flush against the row.
    <View style={styles.section}>
      {!linkedStructure && (
        // textSecondary, not textTertiary: instructions, not a placeholder —
        // textTertiary is sub-AA by design (audit:a11y).
        <Text style={[styles.fallbackLabel, { color: colors.textSecondary }]}>
          Not about a specific property? Set the location on the map.
        </Text>
      )}
      {userLocation ? (
        <TouchableOpacity
          ref={rowRef}
          testID="post-location-row"
          onPress={openPicker}
          disabled={!!linkedStructure}
          activeOpacity={linkedStructure ? 1 : 0.7}
          style={[styles.locationRow, { borderBottomColor: colors.border }]}>
          <MapPin size={16} color={colors.text} />
          <Text style={[styles.locationText, { color: colors.textSecondary }]} numberOfLines={1}>
            {userLocation.address ||
              `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`}
          </Text>
          {!linkedStructure && <Pencil size={14} color={colors.textTertiary} />}
        </TouchableOpacity>
      ) : (
        !linkedStructure && (
          <TouchableOpacity
            ref={rowRef}
            testID="post-location-row"
            onPress={openPicker}
            activeOpacity={0.7}
            style={[styles.locationRow, { borderBottomColor: colors.border }]}>
            <MapPin size={16} color={colors.text} />
            <Text style={[styles.locationText, { color: colors.textSecondary }]} numberOfLines={1}>
              {isLocating ? 'Getting your location…' : 'Drop a pin on the map'}
            </Text>
            <Pencil size={14} color={colors.textTertiary} />
          </TouchableOpacity>
        )
      )}
      {!linkedStructure &&
        (userLocation ? (
          gpsAccuracyM == null ? (
            <Text style={[styles.locationHint, { color: colors.textSecondary }]}>
              Tap to adjust the pin
            </Text>
          ) : (
            // A coarse fix is warned, not whispered: it's the one the composer
            // now keeps rather than discards, and the author is the only one
            // who can tell whether it landed on the right parcel.
            <Text
              style={[
                styles.locationHint,
                { color: isCoarseFix(gpsAccuracyM) ? colors.warning : colors.textSecondary },
              ]}>
              Location accurate to ~{metersToFeetLabel(gpsAccuracyM)} ft. Tap to adjust the pin.
            </Text>
          )
        ) : (
          gpsFailed && (
            <Text style={[styles.locationHint, { color: colors.warning }]}>
              Couldn&apos;t pin your exact location. Tap the row to place the pin on the map.
            </Text>
          )
        ))}
      {linkedStructure && (
        <Text style={[styles.locationHint, { color: colors.textSecondary }]}>
          Location set by linked property
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: SPACING.md,
  },
  // Hairline row, not a tinted box (mockup .rowlink).
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    borderBottomWidth: 1,
    gap: SPACING.sm,
  },
  locationText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
  },
  locationHint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
  },
  fallbackLabel: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.xs,
  },
});
